import React, { forwardRef, useEffect, useState } from 'react';
import numberToWords from '../utils/numberToWords';
import { safeFormatDate } from '../common/dateUtils';
import { getMakeupArtist } from '../services/makeupArtistService';

interface PaymentReceiptProps {
  payment: any; // Accepts payment object with all required fields
  artist: any; // Artist info (name, contact, etc.)
  client: any; // Client info (name, contact, etc.)
  booking?: any; // Optional booking info
  onClose?: () => void;
}

// Forward ref for printing
const PaymentReceipt = forwardRef<HTMLDivElement, PaymentReceiptProps>(({ payment, artist, client, booking, onClose }, ref) => {
  const [resolvedArtistName, setResolvedArtistName] = useState(
    artist?.name || (typeof window !== 'undefined' ? localStorage.getItem('makeupArtistName') : '') || 'Artist'
  );
  const amountInWords = typeof payment.amount === 'number' ? numberToWords(payment.amount) : '';

  // Format receipt number: RYYYYMMId (no dashes)
  let receiptNumber = '';
  if (payment && payment.date && payment.id) {
    const d = new Date(payment.date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    receiptNumber = `R${yyyy}${mm}${payment.id}`;
  }

  useEffect(() => {
    if (artist?.name) {
      setResolvedArtistName(artist.name);
      return;
    }
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('makeupArtistName');
      if (stored) {
        setResolvedArtistName(stored);
        return;
      }
      // Try to fetch from API if not in localStorage
      const idStr = localStorage.getItem('makeupArtistId');
      const id = idStr ? Number(idStr) : 0;
      if (id > 0) {
        getMakeupArtist(id).then((a: any) => {
          if (a?.displayName) {
            setResolvedArtistName(a.displayName);
            localStorage.setItem('makeupArtistName', a.displayName);
          }
        });
      }
    }
  }, [artist]);

  return (
    <div ref={ref} className="bg-white p-8 max-w-lg mx-auto shadow-lg print:shadow-none print:bg-white print:p-0 text-secondary font-sans min-h-[90vh] flex flex-col justify-between">
      {/* Title and Artist Name */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-center text-primary mb-1">Payment Receipt</h2>
        <div className="text-center text-lg font-semibold text-secondary mb-6">{resolvedArtistName}</div>
      </div>
      {/* Header: Left/Right blocks with only bottom border */}
      <div className="flex mb-6">
        {/* Left block */}
        <div className="w-1/2 pr-2 pb-4 border-b border-gray-300">
          <div className="font-semibold text-sm mb-1">Client Name</div>
          <div className="text-base mb-2">{client?.name || '-'}</div>
          <div className="font-semibold text-sm mb-1">Booking No.</div>
          <div className="text-base mb-2">{booking?.bookingNumber || '-'}</div>
        </div>
        {/* Right block */}
        <div className="w-1/2 pl-2 pb-4 text-right border-b border-gray-300">
          <div className="font-semibold text-sm mb-1">Receipt No.</div>
          <div className="text-base mb-2">{receiptNumber || '-'}</div>
          <div className="font-semibold text-sm mb-1">Date</div>
          <div className="text-base">{safeFormatDate(payment.date)}</div>
        </div>
      </div>
      {/* Deta  ils Grid */}
      <div className="mb-2">
        <div className="grid grid-cols-7 gap-2 text-sm font-semibold border-b border-gray-200 pb-1">
          <div className="col-span-4 text-left">Notes</div>
          <div className="col-span-2 text-center">Method</div>
          <div className="col-span-1 text-right">Amount</div>
        </div>
        <div className="grid grid-cols-7 gap-2 text-base py-2 border-b border-gray-300">
          <div className="col-span-4 text-left break-words">{payment.remarks || '-'}</div>
          <div className="col-span-2 text-center">{payment.mode || '-'}</div>
          <div className="col-span-1 text-right font-bold flex items-center justify-end">
            <span className="inline-block align-middle mr-1" title="Rupee">₹</span>{payment.amount}
          </div>
        </div>
        {/* Total row */}
        <div className="grid grid-cols-7 gap-2 text-base font-bold pt-2">
          <div className="col-span-4"></div>
          <div className="col-span-2 text-center">Total</div>
          <div className="col-span-1 text-right text-primary font-bold flex items-center justify-end">
            <span className="inline-block align-middle mr-1" title="Rupee">₹</span>{payment.amount}
          </div>
        </div>
      </div>
      {/* Amount in words */}
      <div className="mt-2 text-sm italic text-gray-700">In Words: {amountInWords} Only/-</div>
      {/* Footer note at the end of the page */}
      <div className="mt-auto pt-10 text-xs text-left text-gray-500 w-full">
        ****This is an online payment receipt. Signature is not required.****
      </div>
      {onClose && (
        <button onClick={onClose} className="mt-6 px-4 py-2 bg-primary text-white rounded print:hidden">Close</button>
      )}
    </div>
  );
});

export default PaymentReceipt;
